import React,{ useCallback, useState, useMemo, useEffect, useRef } from 'react';
import {createBrowserHistory} from 'history';
import {useOktaAuth} from '@okta/okta-react';
import { Menu, MenuItem, MenuDivider, SubMenu } from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import {useAnalytics} from '../../v2/third-party-integration/amplitude';
import * as Styles from './styles';
import {useSideSheet} from '../../contexts/SideSheet';
import { useUserSettingsStore, useRouteStore, useIamUserProfileStore, useGlobalSearchStore, useApplicationStore, 
  useWorkflowsStore,
  useUserIPRestrictionStore,
  useDriveCNumbersStore, useNotificationStore } from '../../Store'
import GlobalSearchRemote from "../GlobalSearchRemote/GlobalSearchRemote";
import { useGetAllMessagingApps } from '../../hooks/queries';
import {
    SIDESHEET_CONTENT,
    SideSheetContent,
    AMPLITUDE_EVENTS,
    LOGOUT_URL,
    EMPTY_STRING,
    URL_ROUTES,
} from '../../constants';
import {
    withTooltip,
    IconHelp,
    THEMES,
    AvatarWrapper,
    AVATAR_SIZES,
    Content,
    createIconWithPath,
    IconRefresh
} from 'cdk-radial';
import {usePaneManagement} from '../../contexts/PaneManagementContext';
import {useUpdateUserAppViewSettings, useUpdateUserSettings, useUserProfile, useAllWorkflowsAndApplications, useUpdateUserLocaleSettings} from '../../hooks/queries';
import {useTranslation} from 'react-i18next';
import IconNotificationButton from './IconNotificationButton';
import {useStore} from './HeaderStore';
import ThemeProvider from '../ThemeProvider/ThemeProvider';
import { useChannelMessage } from '../MessageProvider/MessageContext';
import { LOGOUT_STATUS, MESSAGES, messagingEnabled } from '../MessageProvider/MessageProvider';
import { useTheme } from '../../contexts/Theme';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { setDocumentTitle } from '../../util/TitleManager';
import { Button } from '../Cards/styles';
import { ClickCallbackResponse } from 'global-search/dist/src/components/Distributed/types';
import {useHelpStore} from '../Help/HelpStore';
import { useChannelMessagingStore } from '../../Store/useChannelMessagingStore';
import launchExternal from '../../util/launchExternal';
import { ErrorBoundary } from '../../util/ErrorBoundary';

const history = createBrowserHistory();

export const generateTooltipProps = (iconName:string, displayName:string) => ( 
    {id:`header-tooltip-${iconName}`,text:displayName, theme:THEMES.LIGHT, styles:{ zIndex:1002, display: window.innerWidth < 501 && 'none' }} )

export default function Header() {
  const { setSideSheetContent } = useSideSheet();
  const { activePane, openPanes, isPaneExpaneded, toggleIsPaneExpanded, closePane, setActivePane, openCtaPane} = usePaneManagement();
  const { refetch, isError, data: userInfo  } = useUserProfile();
  const { oktaAuth } = useOktaAuth();
  const { trackEvent } = useAnalytics();
  const { t, i18n } = useTranslation();
  const setCurrentAppInView = useHelpStore((state:any) => state.setCurrentAppInView);

  
  const isUserAppsFetched = useApplicationStore((state:any) => state.isFetched);
  const userApplications = useApplicationStore((state:any) => state.applications);
  const userWorkflows = useWorkflowsStore((state:any) => state.workflows);
  const userIpRestriction = useUserIPRestrictionStore((state: any) => state.ipRestriction);
  const dmsCNumbers = useDriveCNumbersStore((state: any) => state.cNumbers);
  const dmsBoxMapping = useDriveCNumbersStore((state: any) => state.dmsBoxMapping);
    const dmsBoxCNumbers = dmsCNumbers?.map((dms: any) => {
        const currentCNumber = dms?.cNumber;
        const alias = dmsBoxMapping?.dmsBoxMappings?.find(
            (box: any) => box?.cnumber?.toLowerCase() === currentCNumber?.toLowerCase()
        )?.alias;
        return { cNumber: currentCNumber, alias };
    }) ?? [];


  const setHeaderIconClicked = useStore((state:any) => state.setHeaderIconClicked);
  const setLastFocusedElement = useStore((state:any) => state.setLastFocusedElement);
  const lastFocusedElement = useStore((state:any) => state.lastFocusedElement);
  const HeaderHelpIcon = useMemo(() => {
    const tooltipProps = generateTooltipProps('Help', t('help.toolTip'));
          return withTooltip(Styles.HeaderButton, tooltipProps);
      }, [t]);
  const HeaderReloadIcon = useMemo(() => {
    const tooltipProps = generateTooltipProps('Refresh', t('header.refreshchild'));
            return withTooltip(Styles.HeaderButton, tooltipProps);
  }, [t]);  
  const HeaderHistoryIcon = useMemo(() => {
    const tooltipProps = generateTooltipProps('History', t('history.toolTip'));
          return withTooltip(Styles.HeaderButton, tooltipProps);
      }, [t]);
const HeaderNotificationIcon = useMemo(() => {
    const tooltipProps = generateTooltipProps('History', t('history.toolTip'));
          return withTooltip(IconNotificationButton, tooltipProps);
      }, [t]);
      
  const CDKGlobalLogo = withTooltip(Styles.Logo, generateTooltipProps('CDK Global Logo',t('cdklogo.toolTip')));
  const ProfileIcon = withTooltip(AvatarWrapper, generateTooltipProps('User Profile',t('profile.toolTip')));
  const { data:oktaUser } = useUserProfile();
  const [ systemTheme, setSystemTheme ] =  useState(localStorage.getItem('use-system-theme') === 'yes');
  const nameAbbr = (oktaUser?.userProfile?.firstName || '').substring(0,1) + (oktaUser?.userProfile?.lastName || '').substring(0,1);
  const { mutate } = useUpdateUserSettings();
  const {mutate: userAppViewUpdate} = useUpdateUserAppViewSettings();
  const {mutate: userLocaleSettings} = useUpdateUserLocaleSettings();
  const themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const { darkMode } = useTheme();
  const useSystemTheme =  localStorage.getItem('use-system-theme') === 'yes';
  const userPreferenceSettings = useUserSettingsStore((state: any) => state.userSettings);
  const previousRoute =  useRouteStore((state:any) => state.previousRoute);
  const setRoute =  useRouteStore((state:any) => state.setRoute);
  const setPreviousRoute = useRouteStore((state:any) => state.setPreviousRoute);
  const refreshChildAppFlag = useFeatureFlag('REFRESH_CHILD_APP');
  const iamUserInfo = useIamUserProfileStore((state:any) => state.iamUserInfo);
  const adminRole = iamUserInfo.fullUser && iamUserInfo.fullUser.roles && 
                    iamUserInfo.fullUser.roles.find(
                        (role:any) => role.roleName.toLowerCase() === "enterprise administrator");
  const { removeProduct, setLogoutStatus, subscribe, unSubscribe, notifyMessage } = useChannelMessage();
  const { data } = useAllWorkflowsAndApplications();
  const workflows = data.map((app: any) => {
    const workflow = app.appId
      ? {
          ...app,
          logoutUrl:
            data.find((item) => item.id === app.appId)?.logoutUrl || '',
        }
      : { ...app };
    return workflow;
  });
  const [activeAppScriptReady, setActiveAppScriptReady] = useState(false);
  const {data: channelMsgApps} = useGetAllMessagingApps();

  // Custom Theme Icon SVG path, 
  const ThemeIcon = createIconWithPath([{
    d: "M12,22 C17.5228475,22 22,17.5228475 22,12 C22,6.4771525 17.5228475,2 12,2 C6.4771525,2 2,6.4771525 2,12 C2,17.5228475 6.4771525,22 12,22 Z M12,20.5 L12,3.5 C16.6944204,3.5 20.5,7.30557963 20.5,12 C20.5,16.6944204 16.6944204,20.5 12,20.5 Z",
    fillRule: "evenodd",
    fill: darkMode ? 'white' : 'black'
  }]);

  const getProductId = (wfid: string) => {
    let prodId = activePane!;
    if(activePane && activePane.substring(0, 1) === 'F') {
        const workflow = workflows.find((wf) => wf.id === wfid);
        prodId = workflow?.appId || wfid;
    }
    return prodId;
  }

  useEffect(() => {
    setActiveAppScriptReady(false);
  }, [activePane]);

    React.useEffect(() => {
        // Only trigger re-auth on actual user profile fetch errors.
        if (isError && oktaAuth && typeof oktaAuth.signInWithRedirect === 'function') {
            console.log('WhiteScreen: user profile error, triggering re-authentication');
            (async () => {
                try {
                    await oktaAuth?.tokenManager?.clear?.();
                    await oktaAuth.signInWithRedirect({ originalUri: window.location.href });
                } catch (err) {
                    console.error('WhiteScreen: Re-auth failed, falling back to logout:', err);
                    window.location.href = `${LOGOUT_URL}?source=unify`;
                }
            })();
        }
    }, [isError, oktaAuth]);
    
    const toggleTheme = useCallback((theme: 'light' | 'dark') => {
        mutate({ theme });
    }, [mutate]);
    React.useEffect(() => {
        const sysThemeChangeListener = (e:{matches: boolean}) => {
            toggleTheme( e.matches ? "dark" : "light");
        };
        systemTheme && themeMediaQuery?.addEventListener('change', sysThemeChangeListener);
        return () => {
            themeMediaQuery?.removeEventListener('change', sysThemeChangeListener);
        }
    }, [systemTheme, themeMediaQuery, toggleTheme]);

    const msgHandler = (evt: any) => {
        const { id, action } = evt.data;
        const currentPane = JSON.parse(localStorage.getItem('activePane') || '');
        if(id) {
            if (action === MESSAGES.CLOSE_APP) {
                closePane(evt.data.id);
                setActivePane(null);
                removeProduct({ id });
            }
            else if(action === MESSAGES.CLIENT_READY && currentPane === id) {
                setActiveAppScriptReady(true);
            }
        }
    }

    useEffect(() => {
        subscribe({key: 'IID_WATCHER', listener: msgHandler});
        return () => {
            unSubscribe({key: 'IID_WATCHER'});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateAppView = useCallback((appView: string) => {
        userAppViewUpdate({appView});
    }, [userAppViewUpdate]);


    const setInsights = useNotificationStore((state: any) => state.setInsights);

    const updateUserLocale = useCallback((locale: string) => {
        userLocaleSettings({locale});
    }, [userLocaleSettings]);

    const setResultItemSelectedFromResultPage = useGlobalSearchStore((state: any) => state.setResultItemSelectedFromResultPage);
    const handlePane = () => {
        const adminPath =/^\/(admin|pulse|dealer-admin|global-search)/;
        if (adminPath.test(window.location.pathname)) {
            const globalSearchPath =/^\/(global-search)/;
            if (globalSearchPath.test(window.location.pathname)) {
                setActivePane(null);
                setResultItemSelectedFromResultPage(false);
                if (openPanes.length > 0) {
                    setActivePane(openPanes[openPanes.length - 1])
                }
            }

            if(previousRoute === '' || previousRoute === '/admin' || previousRoute === '/dealer-admin' || previousRoute === '/global-search' || previousRoute === '/pulse')
            {
                setRoute('/applications');
                setDocumentTitle(EMPTY_STRING, EMPTY_STRING);
            }
            else{
                setRoute(previousRoute);
            }
            toggleIsPaneExpanded(false);
            localStorage.setItem('activePaneLoaded', JSON.stringify(""));
            window.dispatchEvent(new Event('active-pane-changed'));

            return setPreviousRoute('');
        }
        if(openPanes.length > 0)
        {
        toggleIsPaneExpanded();
        }
        activePane === 'ctapane' && setActivePane(openPanes?.length > 0 ? openPanes[openPanes?.length-1] : '');

        activePane && trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Previous Application'});
        const app =  (workflows || data).find((workflow: any) => workflow.id === activePane);

        if(isPaneExpaneded){
            setCurrentAppInView('unify');
        }else{
           setCurrentAppInView(activePane); 
        }
        if (isPaneExpaneded) {
            setDocumentTitle(EMPTY_STRING, EMPTY_STRING)
            localStorage.setItem('activePaneLoaded', JSON.stringify(""));
            window.dispatchEvent(new Event('active-pane-changed'));
            if(window.location.pathname.indexOf('/bdp') >= 0 ) {
                window.history.replaceState({}, '', '/applications');
            }
        }
        else {
            setDocumentTitle(activePane ? app.name : EMPTY_STRING, EMPTY_STRING);
            localStorage.setItem('activePaneLoaded', JSON.stringify(app?.id || ''));
            window.dispatchEvent(new Event('active-pane-changed'));
        }
    };

    const genSideSheetHandler = (content: SideSheetContent) => () => {
        if (content === 'profile' || content === 'meet_my_cdk_team') {
            const profileButton = document.getElementById("profile") as HTMLElement
            setLastFocusedElement(profileButton);
        }
        else {
            setLastFocusedElement(null);
        }
        content === 'profile' && trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'View Profile'});
        content === 'meet_my_cdk_team' && trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Meet My CDK Team'});
        content === 'notification' && trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Notifications'});
        content === 'history' && trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Opened Apps and Workflows'});
        content === 'help' && trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Help'});

        setSideSheetContent(content);
        if (content && activePane) {
            refetch();
        }
        setHeaderIconClicked(true);
    };
    const Count = openPanes?.length || 0;
    const MenuHandler = (menuAction:string) => {
        switch(menuAction) {
            case 'PROFILE':
                genSideSheetHandler(SIDESHEET_CONTENT.PROFILE)();
                break;
            case 'MEET_MY_CDK_TEAM':
                genSideSheetHandler(SIDESHEET_CONTENT.MEET_MY_CDK_TEAM)();
                break;
            case 'THEME_LIGHT':
            case 'THEME_DARK':
            case 'THEME_SYSTEM':
                handleThemeChange(menuAction);
                break;
            case 'SIGNOUT':
                trackEvent(AMPLITUDE_EVENTS.LOGOUT_UNIFY,{'Action': 'Initiated'});
                setLogoutStatus(LOGOUT_STATUS.LOGOUT_INIT);
                break;
            case 'DEALER_ADMIN':
                openDealerAdmin();
                break;
            case 'default':
            case 'unify':
            case 'applications':
            case 'workflows':
                handleAppUpdate(menuAction);
                break;
            case 'en_US':
            case 'en_CA':
            case 'fr_CA':
                handleLocaleUpdate(menuAction);
                break;   
        }
    }

    const handleAppUpdate = (appId: string) => {
        updateAppView(appId);
        switch(appId) {
            case 'applications':
                setRoute('/applications');
                break;
            case 'workflows':
                setRoute('/workflows')
                break;
            case 'unify':
                setRoute('/myUnify')
                break;
            }
        
        trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Unify View', 'View': appId})
    }

    const handleLocaleUpdate = (locale: string) => {
        updateUserLocale(locale);
        setInsights('', '');
        trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Language', 'Locale': locale})
        //const localeAbbr = locale === 'en_CA' ? 'ca' : locale.substring(0,2);
        i18n.changeLanguage(locale);
        //i18n.changeLanguage(localeAbbr);

        messagingEnabled(getProductId(activePane!), channelMsgApps) && notifyMessage({ action: MESSAGES.UNIFY_PREFERENCE_CHANGED, payload: { type: 'LOCALE', value: locale } });
    }

    const handleThemeChange = (theme: string) => {
        // Remove system theme change handler if already listening
        localStorage.setItem('use-system-theme', 'no');
        let isDarkMode = theme === 'THEME_DARK';
        if(theme === 'THEME_SYSTEM') { 
            isDarkMode = themeMediaQuery?.matches;
            setSystemTheme(true);
            localStorage.setItem('use-system-theme', 'yes');
        }
        else {
            setSystemTheme(false);
        }
        toggleTheme( isDarkMode ? 'dark' : 'light' );
        trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Theme', 'Dark Mode': isDarkMode});
        messagingEnabled(getProductId(activePane!), channelMsgApps) &&  notifyMessage({ action: MESSAGES.UNIFY_PREFERENCE_CHANGED, payload: { type: 'THEME', value: isDarkMode ? 'dark' : 'light' } });
    };

    const openDealerAdmin = () =>{
        toggleIsPaneExpanded(false);
        trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Dealer Admin'});
        const path = window.location.pathname ? window.location.pathname :'/applications';
        if( window.location.pathname !== '/pulse' && window.location.pathname !== '/global-search')
            {
            setPreviousRoute(path);
            }
        history.push('/dealer-admin');
        setRoute('/dealer-admin');
    }

    const reloadActiveFrame = () => {
        try{
            if(messagingEnabled(getProductId(activePane!)!, channelMsgApps)) {
               trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Refresh Page', 'App': activePane});
               const activeFrm:any = document.getElementById(activePane!);
               const appOrigin = appDomains[activePane!] || activeFrm.src;
               activeFrm && activeFrm.contentWindow?.postMessage({id:activePane, action: 'UNIFY_REFRESH', unifyOrigin: document.location.href}, appOrigin);
            }        
        }
        catch(e) {
            console.log(e);
        }
    }

    // Global Search 
    const [query, setQuery] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const oktaToken = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}').accessToken;
    const [userName, setUserName] = useState('');
    const [storeId, setStoreId] = useState('');
    const globalSearchFlag = useFeatureFlag('UNIFY_UI_GLOBAL_SEARCH');
    const searchQuery = useGlobalSearchStore((state: any) => state.searchQuery);
    const setSearchQuery = useGlobalSearchStore((state: any) => state.setSearchQuery);
    const setWasPaneOpened = useGlobalSearchStore((state: any) => state.setWasPaneOpened);
    const setSearchResultItemSelected =  useGlobalSearchStore((state: any) => state.setSearchResultItemSelected);

    useEffect(() => {
        setQuery(searchQuery || '');
    }, [searchQuery]);

    const appDomains = useChannelMessagingStore((state:any) => state.appDomains);

    useEffect(() => {
        setAccessToken(oktaToken?.accessToken);
        setUserName(userInfo?.userProfile?.login || '');
        setStoreId(userInfo?.userProfile?.defaultStore || "");
    }, [oktaToken, userInfo?.userProfile?.login, userInfo?.userProfile?.defaultStore, iamUserInfo?.fullUser?.roles]);

    useEffect(() => {
        const handleRenewed = (key: string, newToken: any) => {
            if (key === 'accessToken' && newToken?.accessToken) {
                setAccessToken(newToken.accessToken);
            }
        };
        oktaAuth?.tokenManager?.on('renewed', handleRenewed);
        return () => {
            oktaAuth?.tokenManager?.off('renewed', handleRenewed);
        };
    }, [oktaAuth]);

    const hideFeedbackAndAiva = () => {
        const feedbackButton = document.querySelector(".QSIFeedbackButton") as HTMLElement | null;
        if (feedbackButton) {
            feedbackButton.style.display = "none";
        }
        const aiva = document.querySelector(".aiva-unify") as HTMLElement | null;
        if (aiva) {
            aiva.style.display = "none";
        }
        const pullupContainer = document.querySelector("#QSIFeedbackButton-target-container") as HTMLElement | null;
        if (pullupContainer) {
            pullupContainer.style.display = "none";
        }
    }

    const handleViewMore = (queryParam: string) => {
        setSearchQuery(queryParam);
        isPaneExpaneded && setWasPaneOpened(true);
        setDocumentTitle(EMPTY_STRING, EMPTY_STRING);
        const path = window.location.pathname ? window.location.pathname : '/applications';
        if (window.location.pathname !== '/pulse'
            && window.location.pathname !== '/global-search'
            && window.location.pathname !== '/dealer-admin'
            && window.location.pathname !== '/global-search-history') {
            setPreviousRoute(path);
        }
        hideFeedbackAndAiva();
        history.push(URL_ROUTES.globalSearch);
        setRoute(URL_ROUTES.globalSearch);
        toggleIsPaneExpanded(false);
        localStorage.setItem('activePaneLoaded', JSON.stringify(""));
        window.dispatchEvent(new Event('active-pane-changed'));
    }

    const handleSearchHistory = () => {
        isPaneExpaneded && setWasPaneOpened(true);
        setDocumentTitle(EMPTY_STRING, EMPTY_STRING);
        const path = window.location.pathname ? window.location.pathname : '/applications';
        if (window.location.pathname !== '/pulse'
            && window.location.pathname !== '/global-search'
            && window.location.pathname !== '/dealer-admin'
            && window.location.pathname !== '/global-search-history') {
            setPreviousRoute(path);
        }
        hideFeedbackAndAiva();
        history.push(URL_ROUTES.globalSearchHistory);
        setRoute(URL_ROUTES.globalSearchHistory);
        toggleIsPaneExpanded(false);
        localStorage.setItem('activePaneLoaded', JSON.stringify(""));
        window.dispatchEvent(new Event('active-pane-changed'));
    }

    const openGlobalSearchItem = (response: ClickCallbackResponse) => {
        try {

            if (response.url.includes("unify") && response.url.endsWith("/admin")) {
                setRoute('/admin');
            }
            else {
                setSearchResultItemSelected(true);
                launchExternal(response.url) || openCtaPane(response.url, response.id);
            }
        } catch {
        }
    };

    const shouldShowGS =
        globalSearchFlag === true &&
        Boolean(accessToken) &&
        isUserAppsFetched === true &&
        Array.isArray(userApplications) &&
        userApplications.length > 0;

  const UNIFY_GS_APPLICATION = useFeatureFlag('UNIFY_GS_APPLICATION');
  const UNIFY_GS_WORKFLOW = useFeatureFlag('UNIFY_GS_WORKFLOW');
  const UNIFY_GS_DOCUMENT = useFeatureFlag('UNIFY_GS_DOCUMENT');
  const UNIFY_GS_CUSTOMER = useFeatureFlag('UNIFY_GS_CUSTOMER');
  const UNIFY_GS_VEHICLE = useFeatureFlag('UNIFY_GS_VEHICLE');
  const UNIFY_GS_VENDOR = useFeatureFlag('UNIFY_GS_VENDOR');
  const UNIFY_GS_DEAL = useFeatureFlag('UNIFY_GS_DEAL');
  const UNIFY_GS_MRDOC = useFeatureFlag('UNIFY_GS_MRDOC');
  const UNIFY_GS_EMPLOYEE = useFeatureFlag('UNIFY_GS_EMPLOYEE');
  const UNIFY_GS_MRCUSTOMER = useFeatureFlag('UNIFY_GS_MRCUSTOMER');
  const UNIFY_UI_GS_NLP = useFeatureFlag('UNIFY_UI_GS_NLP');

  const [harnessFlags, setHarnessFlags] = useState({});

    useEffect(() => {
    setHarnessFlags({
      UNIFY_GS_APPLICATION: UNIFY_GS_APPLICATION,
      UNIFY_GS_WORKFLOW: UNIFY_GS_WORKFLOW,
      UNIFY_GS_DOCUMENT: UNIFY_GS_DOCUMENT,
      UNIFY_GS_CUSTOMER: UNIFY_GS_CUSTOMER,
      UNIFY_GS_VEHICLE: UNIFY_GS_VEHICLE,
      UNIFY_GS_VENDOR: UNIFY_GS_VENDOR,
      UNIFY_GS_DEAL: UNIFY_GS_DEAL,
      UNIFY_GS_MRDOC: UNIFY_GS_MRDOC,
      UNIFY_GS_EMPLOYEE: UNIFY_GS_EMPLOYEE,
      UNIFY_GS_MRCUSTOMER: UNIFY_GS_MRCUSTOMER,
      UNIFY_UI_GS_NLP: UNIFY_UI_GS_NLP
    })
  }, [
    UNIFY_GS_EMPLOYEE,
    UNIFY_GS_MRDOC,
    UNIFY_GS_DEAL,
    UNIFY_GS_VENDOR,
    UNIFY_GS_VEHICLE,
    UNIFY_GS_CUSTOMER,
    UNIFY_GS_DOCUMENT,
    UNIFY_GS_WORKFLOW,
    UNIFY_GS_APPLICATION,
    UNIFY_GS_MRCUSTOMER,
    UNIFY_UI_GS_NLP,
    setHarnessFlags
  ])

    // Global Search End

    useEffect(() => {
        if (lastFocusedElement?.id === 'unify-tab-wfs') {
            setTimeout(() => {
                const nextFocusItem = document.getElementById("aha-widget") as HTMLElement | null;
                nextFocusItem?.focus();
                setLastFocusedElement(null);
            }, 200);
        }
        if (lastFocusedElement?.id === 'unify-tab-apps') {
            setTimeout(() => {
                const nextFocusItem = document.getElementById("unify-tab-wfs") as HTMLElement | null;
                nextFocusItem?.focus();
                setLastFocusedElement(null);
            }, 200);
        }
    }, [lastFocusedElement, setLastFocusedElement]);

    const menuItemClick = (e: any, item: string) => {
        if (e.key === 'Enter') {
            e.stopPropagation();
            MenuHandler(item);
        }
    }

    const CommonMenuItems = () => (<><MenuItem data-testid="header-menu-profile" onKeyDown={(e) => { menuItemClick(e, 'PROFILE')}}  onClick={(e) => {MenuHandler('PROFILE');}} value="PROFILE"><Styles.StyledIconAccountCircle /> <Styles.MenuText>{t('header.viewProfile')}</Styles.MenuText></MenuItem>

        <SubMenu label={<><ThemeIcon style={{ transform: 'rotate(180deg)' }} /> <Styles.MenuText data-testid="header-menu-theme">{t('header.theme')}</Styles.MenuText></>}>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'THEME_LIGHT')}}  onClick={(e) => {MenuHandler('THEME_LIGHT');}} value="THEME_LIGHT">{(!useSystemTheme && !darkMode) && <Styles.CheckIcon />}<Styles.SubMenuText data-testid="header-menu-theme-light">{t('header.light')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'THEME_DARK')}}  onClick={(e) => {MenuHandler('THEME_DARK');}} value="THEME_DARK">{(!useSystemTheme && darkMode) && <Styles.CheckIcon />}<Styles.SubMenuText data-testid="header-menu-theme-dark">{t('header.dark')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'THEME_SYSTEM')}}  onClick={(e) => {MenuHandler('THEME_SYSTEM');}} value="THEME_SYSTEM">{useSystemTheme && <Styles.CheckIcon />} <Styles.SubMenuText data-testid="header-menu-theme-system">{t('header.system')}</Styles.SubMenuText></MenuItem>
        </SubMenu>
        <SubMenu label={<><Styles.StyledIconFlag /> <Styles.MenuText data-testid="header-menu-language">{t('header.language')}</Styles.MenuText></>}>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'en_US')}}  onClick={(e) => {MenuHandler('en_US');}} value="en_US">{userInfo?.locale === "en_US" && <Styles.CheckIcon />}<Styles.SubMenuText data-testid="header-menu-language-eus">{t('English (United States)')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'en_CA')}}  onClick={(e) => {MenuHandler('en_CA');}} value="en_CA">{userInfo?.locale === "en_CA" && <Styles.CheckIcon />}<Styles.SubMenuText data-testid="header-menu-language-eca">{t('English (Canada)')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'fr_CA')}}  onClick={(e) => {MenuHandler('fr_CA');}} value="fr_CA">{userInfo?.locale === "fr_CA" && <Styles.CheckIcon />} <Styles.SubMenuText data-testid="header-menu-language-efr">{t('French (Canada)')}</Styles.SubMenuText></MenuItem>
        </SubMenu>
        <SubMenu label={<><Styles.StyledIconViewCard style={{ transform: 'rotate(180deg)' }} /> <Styles.MenuText data-testid="header-menu-app">{t('header.unifyView')}</Styles.MenuText></>}>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'default')}}  onClick={(e) => {MenuHandler('default');}} value="default">{(userPreferenceSettings.appView === undefined || userPreferenceSettings.appView === 'default') && <Styles.CheckIcon />}<Styles.SubMenuText data-testid="header-menu-app-default">{t('tabs.default')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'unify')}}  onClick={(e) => {MenuHandler('unify');}} value="unify">{(userPreferenceSettings.appView === 'unify') && <Styles.CheckIcon />} <Styles.SubMenuText data-testid="header-menu-app-unify">{t('tabs.myUnify')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'applications')}}  onClick={(e) => {MenuHandler('applications');}} value="applications">{(userPreferenceSettings.appView === 'applications') && <Styles.CheckIcon />}<Styles.SubMenuText data-testid="header-menu-app-applications">{t('tabs.applications')}</Styles.SubMenuText></MenuItem>
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'workflows')}}  onClick={(e) => {MenuHandler('workflows');}} value="workflows">{(userPreferenceSettings.appView === 'workflows') && <Styles.CheckIcon />} <Styles.SubMenuText data-testid="header-menu-app-workflows">{t('tabs.workflows')}</Styles.SubMenuText></MenuItem>
        </SubMenu>
        {adminRole &&
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'DEALER_ADMIN')}}  onClick={(e) => {MenuHandler('DEALER_ADMIN');}} value="DEALER_ADMIN"><Styles.StyledIconAdmin /><Styles.MenuText data-testid="dealer-admin">{t('header.dealerAdmin')}</Styles.MenuText></MenuItem>}
            <MenuItem onKeyDown={(e) => { menuItemClick(e, 'MEET_MY_CDK_TEAM')}}  onClick={(e) => {MenuHandler('MEET_MY_CDK_TEAM');}} value="MEET_MY_CDK_TEAM"><Styles.StyledIconPeople /><Styles.MenuText data-testid="meetmyteam-menu">{t('header.meetMyCdkTeam')}</Styles.MenuText></MenuItem>
        <MenuDivider />
        <MenuItem onKeyDown={(e) => { menuItemClick(e, 'SIGNOUT')}}  onClick={(e) => {MenuHandler('SIGNOUT');}} value="SIGNOUT"><Styles.StyledIconLogout /><Styles.MenuText data-testid="header-menu-signout">{t('header.signout')}</Styles.MenuText></MenuItem>
    </>);
    const menuButtonRef = useRef<HTMLDivElement>(null);
    return (
        <Styles.PageHeader>
            <ThemeProvider>
            <div>
                {
                    isPaneExpaneded && Count ? 
                            <CDKGlobalLogo
                                height="32"
                                viewBox="0 0 300 74"
                                width="116"
                                text={t('cdklogo.tooltip')}
                                data-testid="global-header-logo"
                                onClick={handlePane}
                                onTouch={handlePane}
                                onKeyDown={(e: any) => {
                                    if (e.key === 'Enter') {
                                        handlePane()
                                    }
                                }}
                                tabIndex={0}
                            />
                 :
                            <Styles.Logo
                                data-testid="global-header-logo"
                                onClick={handlePane}
                                height="32"
                                viewBox="0 0 300 74"
                                width="116"
                                tabIndex={0}
                                onKeyDown={(e: any) => {
                                    if (e.key === 'Enter') {
                                        handlePane()
                                    }
                                }}
                            />
                }
            </div>
                {shouldShowGS ?
               ( <ErrorBoundary>
                    <GlobalSearchRemote
                        variant="search"
                        
                        accessToken={accessToken}
                        onChange={(value: string) => setQuery(value)}
                        query={query}
                        storeId={storeId}
                        userName={userName}
                        viewMore={handleViewMore}
                        viewSearchHistory={handleSearchHistory}
                        onClickCallback={openGlobalSearchItem}
                        lng={i18n.language}
                        unifyUserApplications={userApplications}
                        unifyUserWorkflows={userWorkflows}
                        ipRestrictionDetails={userIpRestriction}
                        harnessFlags={harnessFlags}
                        cNumbers={dmsBoxCNumbers}
                        /> 
                        </ErrorBoundary>)
                        :
                        <div></div>}
                <Styles.PageHeaderIcons>
                    {refreshChildAppFlag && activePane && isPaneExpaneded && activeAppScriptReady && 
                        <HeaderReloadIcon
                            onClick={reloadActiveFrame}
                            backgroundAppearance={'dark'}
                            icon={<IconRefresh />}
                            text={t('header.refreshchild')}
                            className={!isPaneExpaneded && 'animated-apps-icon'}
                            dataTestId="header-icon-reload"
                        />
                    }
                    <HeaderHistoryIcon
                        onClick={genSideSheetHandler(SIDESHEET_CONTENT.HISTORY)}
                        backgroundAppearance={'dark'}
                        icon={<Styles.ApplicationAnimatedIcon/>}
                        text={Count}
                        className={!isPaneExpaneded && 'animated-apps-icon'}
                        dataTestId="header-icon-history"
                    />
                    <HeaderHelpIcon
                        onClick={genSideSheetHandler(SIDESHEET_CONTENT.HELP)}
                        backgroundAppearance={'dark'}
                        icon={<IconHelp/>}
                        text={t('header.help')}
                        dataTestId="header-icon-help"
                    />
                    <HeaderNotificationIcon onClick={genSideSheetHandler(SIDESHEET_CONTENT.NOTIFICATION)} tabIndex={0}/>
                    <Button id="profile" style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer'
                    }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                                const menu = document.getElementById("profileButton") as HTMLElement | null;
                                const profileButton = document.getElementById("profile") as HTMLElement
                                setLastFocusedElement(profileButton);
                                menu?.click();
                            }}}>
                        <Menu
                            ref={menuButtonRef}
                            theming={darkMode ? 'dark' : ''}
                            boundingBoxPadding={'0 4px 0 0'}
                            portal={true} align='start'
                            id="szh-menu"
                            transition
                            tabIndex={-1}
                            onMenuChange={({ open }) => {
                                if (!open) {
                                    setTimeout(() => {
                                        lastFocusedElement?.focus();
                                    }, 50);
                                }
                            }}
                            menuButton={<div data-testid="header-menu-profile-icon"
                                onClick={() => trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, { 'Item': 'Profile' })}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                        e.currentTarget.click();
                                    }
                                }}
                                tabIndex={-1}
                            ><ProfileIcon size={AVATAR_SIZES.SMALL} tabIndex={-1} id="profileButton"
                            style={{ backgroundColor: '#F2F2F2', margin: '2px' }}>
                                <Content style={{
                                    color: 'black',
                                    cursor: 'pointer',
                                }} tabIndex={-1}>{nameAbbr}</Content>
                            </ProfileIcon></div>} >
                        <CommonMenuItems></CommonMenuItems>
                    </Menu>
                </Button>
                </Styles.PageHeaderIcons> 
            </ThemeProvider>
        </Styles.PageHeader>
    );
}

